import {
  ATTENDANCE_PUNCHES,
  DRIVER_PROFILES,
  deriveTimesheetsFromPunches,
  TIMESHEET_ALLOWANCES,
} from './drivers/mockDriverErpData';
import { computeAutomatedPayroll } from './drivers/driverPayrollUtils';

const PAYROLL_YEAR = 2026;
const PAYROLL_MONTH = 5;

/** Driver performance scorecards — logistics optimizer micro-cards */
export const DRIVER_SCORECARDS = {
  'DRV-001': { tripAccuracy: '98%', fuelEfficiency: 'Top Tier', safetyRecord: 'Clean' },
  'DRV-002': { tripAccuracy: '96%', fuelEfficiency: 'Top Tier', safetyRecord: 'Clean' },
  'DRV-003': { tripAccuracy: '94%', fuelEfficiency: 'Standard', safetyRecord: 'Clean' },
  'DRV-004': { tripAccuracy: '97%', fuelEfficiency: 'Top Tier', safetyRecord: 'Clean' },
  'DRV-005': { tripAccuracy: '92%', fuelEfficiency: 'Standard', safetyRecord: '1 Minor Flag' },
  'DRV-006': { tripAccuracy: '99%', fuelEfficiency: 'Top Tier', safetyRecord: 'Clean' },
  'DRV-007': { tripAccuracy: '95%', fuelEfficiency: 'Top Tier', safetyRecord: 'Clean' },
  'DRV-008': { tripAccuracy: '93%', fuelEfficiency: 'Standard', safetyRecord: 'Clean' },
  'DRV-009': { tripAccuracy: '91%', fuelEfficiency: 'Standard', safetyRecord: 'Clean' },
  'DRV-010': { tripAccuracy: '97%', fuelEfficiency: 'Top Tier', safetyRecord: 'Clean' },
};

const DEFAULT_SCORECARD = {
  tripAccuracy: '95%',
  fuelEfficiency: 'Standard',
  safetyRecord: 'Clean',
};

export function getDriverScorecard(driverId) {
  return DRIVER_SCORECARDS[driverId] || DEFAULT_SCORECARD;
}

/** Build payroll-enriched driver roster sorted by trips completed (desc) */
export function buildDriverPerformanceRoster() {
  const punches = ATTENDANCE_PUNCHES;
  const timesheets = deriveTimesheetsFromPunches(punches, TIMESHEET_ALLOWANCES);

  return DRIVER_PROFILES.map((driver) => {
    const payroll = computeAutomatedPayroll(
      driver,
      punches,
      timesheets,
      PAYROLL_YEAR,
      PAYROLL_MONTH,
    );
    return {
      id: driver.id,
      name: driver.name,
      tripsCompleted: payroll.computed.tripCount,
      overtimeHours: payroll.computed.totalOvertimeHours,
    };
  }).sort((a, b) => b.tripsCompleted - a.tripsCompleted);
}

/**
 * Pick vendor with highest active fleet availability.
 * @param {import('./mockTransportVendorData').TRANSPORT_VENDORS} vendors
 */
export function pickBestAvailableVendor(vendors) {
  const active = vendors.filter((v) => v.contractStatus === 'Active');
  if (!active.length) return null;
  return [...active].sort((a, b) => b.totalFleets - a.totalFleets)[0];
}

/**
 * Pick driver with highest trips completed from payroll engine.
 * @param {ReturnType<typeof buildDriverPerformanceRoster>} roster
 */
export function pickBestPerformingDriver(roster) {
  if (!roster.length) return null;
  return roster[0];
}

/**
 * Mock smart-match optimizer — returns best vendor + driver pair.
 */
export function runSmartMatchAlgorithm(activeVendors, driverRoster) {
  const bestVendor = pickBestAvailableVendor(activeVendors);
  const bestDriver = pickBestPerformingDriver(driverRoster);
  return {
    vendorId: bestVendor?.vendorId || '',
    driverId: bestDriver?.id || '',
    vendorName: bestVendor?.companyName,
    driverName: bestDriver?.name,
    driverTrips: bestDriver?.tripsCompleted ?? 0,
    vendorFleets: bestVendor?.totalFleets ?? 0,
  };
}
